// src/pages/Dashboard.jsx
import React, { useState, useCallback } from "react";
import { Box, Paper, Stack, CircularProgress, Alert, Typography } from "@mui/material";
import useAuthCheck from "../hooks/useAuthCheck";

// Import komponen
import DashboardHeader from "./dashboard/header/DashboardHeader";
import WeatherCalendar from "./dashboard/feature/WeatherCalendar";
import FilterPanel from "./dashboard/feature/FilterPanel";
import SectionLabita from "./dashboard/section-labita/SectionLabita";
import SectionPlantation from "./dashboard/section-plantation/SectionPlantation";
import SectionBoomSpray from "./dashboard/section-boomspray/SectionBoomSpray";
import SectionObservation from "./dashboard/section-observation/SectionObservation";
import SectionHarvesting from "./dashboard/section-harvesting/SectionHarvesting";
import SectionProductions from "./dashboard/section-productions/SectionProductions";
import SectionStok from "./dashboard/section-stok/SectionStok";

// Import utility functions
import { getUserFromLocalStorage } from "./dashboard/userDataUtils";

// Import fungsi tanggal yang sudah dimodularisasi
import { formatDate } from "./dashboard/dateUtils";

import "./dashboard/css/ded-dashboard.css";
import dayjs from "dayjs";

function Dashboard() {
    const { user, loading: authLoading } = useAuthCheck();
    const [calendarOpen, setCalendarOpen] = useState(false);
    const [filterOpen, setFilterOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState(dayjs());

    // State untuk filter - dipindahkan ke sini untuk menghindari circular dependency
    const [selectedFilters, setSelectedFilters] = useState({
        wilayah: null,
        kepalaKebun: null,
        statusLokasi: null,
        jenisBibit: null,
        kelasBibit: null
    });

    // Wrapper function untuk menggunakan user.uid
    const getUserData = useCallback(() => {
        return getUserFromLocalStorage(user?.uid);
    }, [user?.uid]);

    // Handler untuk toggle kalender
    const handleCalendarClick = () => {
        setCalendarOpen(!calendarOpen);
    };

    // Handler untuk toggle filter
    const handleFilterClick = (newFilterState) => {
        setFilterOpen(newFilterState);
    };

    // Handler untuk memilih tanggal dari DateCalendar
    const handleDateChange = (newDate) => {
        setSelectedDate(newDate);
    };

    // Handler untuk update filter
    const updateFilter = (filterKey, value) => {
        setSelectedFilters(prev => ({
            ...prev,
            [filterKey]: value
        }));
    };

    // Handler untuk clear semua filter
    const clearAllFilters = () => {
        setSelectedFilters({
            wilayah: null,
            kepalaKebun: null,
            statusLokasi: null,
            jenisBibit: null,
            kelasBibit: null
        });
    };

    // Mendapatkan informasi tanggal untuk ditampilkan di chip
    const dateInfo = formatDate(selectedDate);

    // Shadow style yang konsisten
    const paperShadow =
        "0px 2px 1px -1px rgba(0, 0, 0, 0.2), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12)";

    if (authLoading) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!user) {
        return (
            <Box sx={{ p: 3 }}>
                <Alert severity="error">Anda harus login untuk mengakses dashboard</Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ pb: 3, pt: 0, width: '100%' }}>
            {/* Header Dashboard */}
            <DashboardHeader
                user={user}
                getUserData={getUserData}
                selectedDate={selectedDate}
                dateInfo={dateInfo}
                calendarOpen={calendarOpen}
                onCalendarClick={handleCalendarClick}
                onFilterClick={handleFilterClick}
                formatDate={formatDate}
            />

            {/* Weather & Calendar Component */}
            <Box sx={{ width: '100%', overflow: 'hidden' }}>
                <WeatherCalendar
                    calendarOpen={calendarOpen}
                    selectedDate={selectedDate}
                    onDateChange={handleDateChange}
                    paperShadow={paperShadow}
                />
            </Box>

            {/* Filter Panel Component */}
            <Box sx={{ width: '100%', overflow: 'hidden' }}>
                <FilterPanel
                    filterOpen={filterOpen}
                    paperShadow={paperShadow}
                    selectedFilters={selectedFilters}
                    updateFilter={updateFilter}
                    clearAllFilters={clearAllFilters}
                />
            </Box>

            {/* Area konten Labita */}
            <SectionLabita paperShadow={paperShadow} />

            {/* Section Plantation */}
            <SectionPlantation
                paperShadow={paperShadow}
                filterOpen={filterOpen}
                selectedFilters={selectedFilters}
            />

            <SectionBoomSpray paperShadow={paperShadow}/>
            <SectionObservation paperShadow={paperShadow}/>
            <SectionHarvesting paperShadow={paperShadow}/>
            <SectionProductions paperShadow={paperShadow}/>
            <SectionStok paperShadow={paperShadow}/>
        </Box>
    );
}

export default Dashboard;